<template>
  <div v-show="hasModel3D" class="canvas3d-wrapper">
    <v-fade-transition>
      <v-overlay
        v-if="!installed || saving3dDesign || loading"
        absolute
        opacity="1"
        color="white"
        z-index="0"
      >
        <v-row>
          <v-col class="d-flex justify-center actual-logo-loading">
            <v-img :src="actualLogo" />
          </v-col>
        </v-row>
        <v-row align="center" justify="center">
          <v-col class="d-flex justify-center">
            <v-progress-circular
              indeterminate
              color="primary"
            ></v-progress-circular>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="d-flex justify-center">
            <span class="aviso-canvas-3d">
              {{
                saving3dDesign || loading
                  ? $t("general.canvas3dprocess")
                  : $t("general.canvas3dloading")
              }}
            </span>
          </v-col>
        </v-row>
      </v-overlay>
    </v-fade-transition>
    
    <div
      v-show="activeCanvas3d && active"
      id="canvas3d"
      :key="'canvas-' + modelCode"
      class="w-100"
    ></div>
    <div v-if="activeCanvas3d && active" class="floating-screenshot-btn">
      <cotizador-btn
        dark rounded 
        content-class="px-0 mb-1 btn-screenshot" size="24" :on="{
        click: takeScreenshot,
        mouseover: hoverScreenshotBtn,
        mouseleave: () => { hoverScreenshotBtn(false) }
      }"><v-icon :class="{'pl-2':  onHoverDownload}">$fi-download</v-icon>
      <v-slide-x-transition>
        <span v-if="onHoverDownload" class="mx-2">{{ $t('general.exportToPng') }}</span>
      </v-slide-x-transition>
      </cotizador-btn>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { saveAs } from "file-saver";

import CotizadorBtn from "@/components/basic/CotizadorBtn.vue";

import {
  init3DCanvas,
  loadColor,
  setActiveZone,
  uploadFile,
  uploadText,
  resetCanvas3d,
  removeImg,
  removeText,
  getActualScreenshot,
  findZoneBy3dId,
  estimateRealMmDimensions
} from "@/utils/customDimUtils";

export default {
  components: {
    CotizadorBtn
  },
  props: {
    id: {
      type: [String, Number],
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    zona: {
      type: Object,
      required: true
    },
    img: {
      type: Object,
      default: null
    },
    active: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      installed: false,
      json: {}, // JSON con la información de la personalización 3D
      activeZone3D: null, // Zona activa en el canvas 3D
      imgInfoCanvas3d: {}, // Diccionario para poder borrar el logo actual del canvas
      activeColorHexcode: null, // Hexcode del color actual, para evitar re activar el cambio de color, si no ha cambiado

      // Objetos para hacer debounced
      debouncedChangeColor: null,
      debouncedChangeZone: null,
      debouncedChangeImg: null,

      updatingImgOnDeleteZone: false, // Variable para controlar si estamos actualizando la imagen al borrar una zona
      onHoverDownload: false, // Variable para controlar si estamos en hover del botón de descarga

      loading: 0, // Variable para controlar si estamos realizando algún proceso en el 3D,

      initialicePromise: Promise.resolve() // Promesa donde vamos a almacenar si estamos inicializando el canvas
    };
  },
  computed: {
    ...mapGetters("modelo", [
      "modelCode",
      "idModel3D",
      "hasModel3D",
      "actualColorInfo",
      "zonas",
      "colorAvalible"
    ]),
    ...mapGetters("config", ["actualLogo", "saving3dDesign"]),
    ...mapGetters("design", ['activeZonesId', 'actualListDesign', 'activeZoneMaxSize']),

    /**
     * Indica si el canvas 3D está activo
     */
    activeCanvas3d() {
      return (
        process.env.VUE_APP_ACTIVE_3D == "true" &&
        this.modelCode &&
        this.hasModel3D &&
        this.installed
      );
    },
    /**
     * Hexcode del color seleccionado
     */
    hexcodeColorSelected() {
      return this.actualColorInfo?.hexcode3D ?? this.actualColorInfo?.hexcode;
    }
  },
  watch: {
    // json: {
    //   handler(newVal) {
    //     console.log(newVal);
    //   },
    //   deep: true
    // },
    imgInfoCanvas3d: {
      handler(newVal) {
        this.updateImgInfoCanvas3d(newVal);
      },
      deep: true
    },
    activeZone3D: {
      handler(newVal) {
        this.updateActiveZone3D(newVal);
      },
      deep: true
    },
    loading(newVal) {
      if(newVal < 0)
        this.loading = 0;
    },
    activeZonesId: {
      deep: true,
      async handler(newVal, oldVal) {
        if (newVal?.length == oldVal?.length && newVal?.length > 0) {
          for (let i = 0; i < newVal?.length; i++) {
            if (newVal[i] == oldVal[i]) {
              return;
            }
          }
        }

        // Si el nuevo valor, hemos borrado alguna entrada. La buscamos y borramos su imagen si existe
        if (newVal?.length < oldVal?.length) {
          for (let i = 0; i < oldVal?.length; i++) {
            if (oldVal[i].id && !newVal.includes(oldVal[i])) {

              // Tenemos que obtener la imagen de la zona a borrar
              // Buscamos en el listado de diseños nuestro
              let idZonaABorrar = oldVal[i].id;
              let imgABorrar = this.actualListDesign.find(
                (img) => img.id == idZonaABorrar
              )?.idImg;

              if (!imgABorrar) {
                console.warn(
                  "💻 ~ No hemos podido encontrar la imagen a borrar",
                  idZonaABorrar
                );
                return;
              }

              // Obtenemos información 3D de la imagen
              let info3dImgABorrar = this.imgInfoCanvas3d[imgABorrar];

              if (!info3dImgABorrar) {
                console.warn(
                  "💻 ~ No hemos podido encontrar la imagen a borrar",
                  imgABorrar
                );
                return;
              }

              this.updatingImgOnDeleteZone = true;

              if (this.debouncedChangeZone) {
                clearTimeout(this.debouncedChangeZone);
              }

              let oldActiveZone = await setActiveZone(oldVal[i]);

              if (info3dImgABorrar.mode == "IMG") {
                await removeImg(
                  info3dImgABorrar,
                  oldActiveZone
                );
              }
              else if (info3dImgABorrar.mode == "TEXT") {
                await removeText(info3dImgABorrar, oldActiveZone)
              }
              
              this.$delete(this.imgInfoCanvas3d, imgABorrar);

              await setActiveZone(this.zona);

              this.updatingImgOnDeleteZone = false;
            }
          }
        }
      },
    },
    async hexcodeColorSelected(newVal, oldVal) {
      if (newVal == oldVal) return;

      if (this.activeCanvas3d) {
        await this.updateColorCanvas3d(newVal);
      }
    },
    async img(newVal, oldVal) {
      // console.log("IMG UPDATE WATCH", {
      //   id: newVal?.id,
      //   mode: newVal?.mode,
      //   text: newVal?.textDesign,
      //   base64: newVal?.base64
      // }, {
      //   id: oldVal?.id,
      //   mode: oldVal?.mode,
      //   text: oldVal?.textDesign,
      //   base64: oldVal?.base64
      // })

      if (newVal?.id && newVal.id == oldVal?.id) {
        console.warn("No debemos hacer nada, mismo id");
        return;
      }

      if (this.debouncedChangeImg) {
        clearTimeout(this.debouncedChangeImg);
        this.loading--;
      }

      // Tenemos que controlar la condición de carrera de cambiar de zona que no se borre la imagen
      if (!this.activeZone3D || this.activeZone3D?.sku != this.zona.Posicion)
        return;

      // Guardamos todo en variables, ya que puede cambiar su estado, pero la ejecución debe mantenerse
      let localImgInfoCanvas3d = this.imgInfoCanvas3d;
      let localActiveZone3D = this.activeZone3D;
      let localNewVal = newVal;
      let localOldVal = oldVal;

      this.loading++;

      this.debouncedChangeImg = setTimeout(async () => {
      //   console.log("TIMEOUT -> IMG UPDATE WATCH", {
      //   id: localNewVal?.id,
      //   mode: localNewVal?.mode,
      //   text: localNewVal?.textDesign,
      //   base64: localNewVal?.base64
      // }, {
      //   id: localOldVal?.id,
      //   mode: localOldVal?.mode,
      //   text: localOldVal?.textDesign,
      //   base64: localOldVal?.base64
      // })
      //   console.log("🚀 ~ localOldVal?.id:", localOldVal?.id)
      //   console.log("🚀 ~ localOldVal?.base64 || localOldVal?.textDesign:", localOldVal?.base64 || localOldVal?.textDesign)
      //   console.log("🚀 ~ localOldVal.id != localNewVal?.id:", localOldVal?.id != localNewVal?.id)
        if (localOldVal?.id && (localOldVal?.base64 || localOldVal?.textDesign) && localOldVal.id != localNewVal?.id) {
          if (this.zona?.id != localOldVal.zona?.id) {
            console.warn("No coinciden los ids de la zona y la imagen a borrar")
          }
          else {
            // Debemos borrar la imagen previamente en el canvas
            if (!localImgInfoCanvas3d[localOldVal?.id]) {
              console.warn(
                "💻 ~ No tenemos un imgInfoCanvas3d para borrar la imagen",
                localOldVal?.id
              );
              this.loading--;
              return;
            }
            if (localImgInfoCanvas3d[localOldVal?.id].mode == "IMG") {
              await removeImg(
                localImgInfoCanvas3d[localOldVal?.id],
                localActiveZone3D
              );
            }
            else if (localImgInfoCanvas3d[localOldVal?.id].mode == "TEXT") {
              await removeText(localImgInfoCanvas3d[localOldVal?.id],
                localActiveZone3D)
            }
            
            console.log("🚀 ~ this.imgInfoCanvas3d:", this.imgInfoCanvas3d, localOldVal)
            this.$delete(this.imgInfoCanvas3d, localOldVal?.id);
          }
        }

        if (localImgInfoCanvas3d[localNewVal?.id]) {
          console.warn(
            "💻 ~ Ya tenemos una imagen en el canvas 3D, no la volvemos a cargar",
            localNewVal?.id
          );
          this.loading--;
          return;
        }
          
          // console.log("TEST Add image/text", this.activeCanvas3d &&
          // localNewVal &&
          // (localNewVal.base64 || localNewVal.textDesign) &&
          // localOldVal?.id != localNewVal?.id)
        // Si tenemos una nueva imagen, la añadimos al canvas 3D
        if (
          this.activeCanvas3d &&
          localNewVal &&
          (localNewVal.base64 || localNewVal.textDesign) &&
          localOldVal?.id != localNewVal?.id
        ) {
          await this.updateImgCanvas3d(localNewVal);
          // console.error("TODO: Actualizar imagen en canvas 3D");
        }

        this.loading--;
        }, 600);
    },
    installed(newVal, oldVal) {
      if (newVal == oldVal) return;

      if (newVal) {
        this.$emit("show-canvas");
      } else {
        this.$emit("close-canvas");
      }
    },
    async zona(newVal, oldVal) {
      if (newVal?.Posicion == oldVal?.Posicion) return;

      if (!newVal || !newVal?.Posicion) {
        console.warn("💻 ~ Necesitamos un valor de posicion correcto", newVal);
        return;
      }

      await this.setZone3d(newVal);
    },
    idModel3D(newVal, oldVal) {
      if (newVal == oldVal) return;

      this.disableCanvas3d();

      if (newVal) {
        setTimeout(async () => {
          await this.load3D();
          await this.updateColorCanvas3d();
        }, 500);
      }
    }
  },
  async mounted() {
    await this.load3D();
  },
  beforeDestroy() {
    this.disableCanvas3d();
  },
  methods: {
    ...mapActions('design', ['updateImgInfoCanvas3d', 'updateActiveZone3D', 'rotateLogoState']),
    /**
     * Carga un modelo 3D en caso de que ese modelo tenga 3D
     */
    async load3D() {
      if (!this.hasModel3D) {
        console.warn("💻 ~ No tenemos un modelo 3D para cargar");
        return;
      }

      if (!this.installed && this.idModel3D) {
        this.initialicePromise = this.initialicePromise.then(() => {
          return new Promise((resolve, reject) => {
            try {
              init3DCanvas(this.json, this.idModel3D, {
                rotateImage3D: this.rotateCallBack,
                scaleImage3D: this.scaleCallBack,
                rotateText3D: this.rotateCallBack,
                scaleText3D: this.scaleCallBack
              }).then(() => {
                this.installed = true;
                return this.updateColorCanvas3d()
              }).then(resolve).catch(reject);
              
            } catch (err) {
              console.error(err);
              reject(err)
            }
            
          })
        })
        
      }
    },

    /**
     * Actualiza el color del canvas 3D.
     *
     * @param {string|null} hexcodeColorSelected - El código hexadecimal del color seleccionado. Si no se proporciona, se utilizará this.hexcodeColorSelected.
     *
     * Este método actualiza el color del canvas 3D utilizando el código hexadecimal proporcionado. Si no se proporciona un código hexadecimal, se utiliza el valor de this.hexcodeColorSelected.
     * Se utiliza un temporizador para evitar múltiples actualizaciones rápidas. Si el color seleccionado es el mismo que el color activo, no se realiza ninguna acción.
     * Después de cargar el nuevo color, se actualiza la zona activa en el canvas 3D.
     */
    async updateColorCanvas3d(hexcodeColorSelected = null) {
      if (!hexcodeColorSelected) {
        hexcodeColorSelected = this.hexcodeColorSelected;
      }

      if (this.colorAvalible?.length <= 1) {
        console.warn("Solo tenemos un color disponible, no debemos cambiar de color");
        this.activeColorHexcode = hexcodeColorSelected;
        return;
      }

      if (this.debouncedChangeColor) {
        clearTimeout(this.debouncedChangeColor);
        this.loading--;
      }

      this.loading++;
      this.debouncedChangeColor = setTimeout(async () => {
        if (hexcodeColorSelected == this.activeColorHexcode) return;

        if(!this.activeCanvas3d) {
          console.warn("💻 ~ No tenemos un canvas 3D activo para actualizar el color");
          return;
        }

        if (hexcodeColorSelected.includes("/")) {
          await loadColor(this.actualColorInfo?.code,  'colorcode');
        }
        else
        {
          try {
            await loadColor(hexcodeColorSelected);
          }
          catch(err) {
            console.log("Error al intentar cambiar el color, intentamos el fallback")
            await loadColor(this.actualColorInfo?.code,  'colorcode');
          }
        }

        this.activeColorHexcode = hexcodeColorSelected;

        setTimeout(async () => {
          this.activeZone3D = await setActiveZone(this.zona);
          this.loading--;
        }, 1000);
      }, 500);
    },
    /**
     * Establece la zona 3D activa después de un retraso debounced.
     *
     * @param {Object} zone - La zona que se va a establecer como activa.
     * @returns {Promise<void>} - Una promesa que se resuelve cuando la zona activa ha sido establecida.
     *
     * Esta función establece la zona 3D activa después de un retraso de 450 ms para evitar múltiples cambios rápidos.
     * Si ya hay un cambio de zona en proceso, se cancela el anterior.
     */
    async setZone3d(zone) {
      if (this.activeCanvas3d && !this.updatingImgOnDeleteZone) {
        if (this.debouncedChangeZone) {
          clearTimeout(this.debouncedChangeZone);
        }

        this.debouncedChangeZone = setTimeout(async () => {
          try {
            // Ensure the canvas has finished initializing before changing zone
            await this.initialicePromise;
            console.debug('💻 ~ setZone3d ~ setting active zone after initialization', zone);
            this.activeZone3D = await setActiveZone(zone);
            console.debug('💻 ~ setZone3d ~ active zone result', this.activeZone3D);
          } catch (err) {
            console.error('💻 ~ setZone3d ~ Error setting active zone:', err, zone);
          }
        }, 500);
      }
    },
    /**
     * Actualiza la imagen del canvas 3D con una nueva imagen en base64.
     *
     * @param {string} base64 - La imagen en formato base64 que se va a cargar en el canvas 3D.
     * @returns {Promise<void>} - Una promesa que se resuelve cuando la imagen ha sido cargada y el evento ha sido emitido.
     *
     * @emits save-design - Emite este evento cuando la imagen ha sido guardada exitosamente.
     */
    async updateImgCanvas3d({ base64, id, textDesign, fontSelected, colorPicker }) {
      if (!this.activeZone3D) {
        console.warn(
          "💻 ~ No hemos podido cargar la zona correcta para el canvas 3d"
        );
        return;
      }
      this.loading++;
      if (!textDesign) {
        this.$set(this.imgInfoCanvas3d, id, await uploadFile(base64, this.activeZone3D, this.activeZoneMaxSize.Ancho, this.activeZoneMaxSize.Alto));
      }
      else
      {
        this.$set(this.imgInfoCanvas3d, id, await uploadText(textDesign, this.activeZone3D, this.activeZoneMaxSize.Ancho, this.activeZoneMaxSize.Alto, fontSelected, colorPicker))
      }
      this.loading--;

      if (base64 || textDesign) {
        this.$emit("save-design", {
          zoneToSave: this.zona
        });
      }
    },
    /**
     * Desactiva el canvas 3D y elimina todos los elementos dentro del div con id "canvas3d".
     *
     * Este método realiza las siguientes acciones:
     * 1. Establece la propiedad `installed` a `false`.
     * 2. Obtiene el elemento con id "canvas3d" del DOM.
     * 3. Si el elemento existe, elimina todos sus elementos hijos.
     * 4. Llama a la función `resetCanvas3d` para restablecer el estado del canvas 3D.
     */
    disableCanvas3d() {
      this.initialicePromise.then(() => {
        this.installed = false;

        // Eliminamos todos los elementos dentro del div canvas3d
        let canvas3d = document.getElementById("canvas3d");
        if (canvas3d) {
          while (canvas3d.firstChild) {
            canvas3d.removeChild(canvas3d.firstChild);
          }
        }

        resetCanvas3d();
      })
    },

    /**
     * Toma una captura de pantalla del canvas 3D y la guarda como un archivo JPEG.
     * 
     * - Obtiene la captura de pantalla en formato base64 llamando a `getActualScreenshot()`.
     * - Si no se puede obtener la captura, muestra un mensaje de advertencia en la consola.
     * - Utiliza la función `saveAs` para descargar el archivo con el nombre generado.
     * 
     * @returns {Promise<void>} No devuelve ningún valor, pero puede mostrar advertencias en la consola.
     */
    async takeScreenshot() {
      this.loading++;
      let actualBase64 = await getActualScreenshot();

      if(!actualBase64) {
        console.warn("No hemos podido obtener la captura de pantalla");
        return;
      }

      const iso = new Date().toISOString();
        let formattedDate =
          iso.slice(2, 10).replace(/-/g, "") +
          "_" +
          iso.slice(11, 16).replace(/:/g, "");

      let base64Data = actualBase64.split(",")[1]; // Remove the prefix
      let binaryData = atob(base64Data); // Decode base64 to binary
      let arrayBuffer = new Uint8Array(binaryData.length);
      for (let i = 0; i < binaryData.length; i++) {
        arrayBuffer[i] = binaryData.charCodeAt(i);
      }
      let blob = new Blob([arrayBuffer], { type: "image/jpeg" });
      saveAs(blob, "screenshot_" + formattedDate + ".jpeg");
      this.loading--;
    },

    /**
     * Cambia el estado del botón de captura de pantalla al pasar el cursor sobre él.
     *
     * @param {boolean} [payload=true] - Indica si el botón está siendo resaltado (true) o no (false).
     */
    hoverScreenshotBtn(payload = true) {
      this.onHoverDownload = payload;
    },

    scaleCallBack(event) {

      if (!event) {
        console.warn("💻 ~ No hemos podido obtener el evento de escala");
        return;
      }

      if (!event.info?.idPart) {
        console.warn("💻 ~ No hemos podido obtener el id de la zona", event);
        return;
      }

      let zone3D = findZoneBy3dId(event.info?.idPart);

      if (!zone3D) {
        console.warn("💻 ~ No hemos podido encontrar la zona", event.info?.idPart);
        return;
      }

      let scaleImg;

      // Buscamos la imagen a la que le pertenece con el path y la zona en imgInfoCanvas3d
      Object.keys(this.imgInfoCanvas3d).forEach((imgKey) => {
        let img = this.imgInfoCanvas3d[imgKey];


        if (img?.url && img?.url == event.info.path) {
          scaleImg = imgKey;

          return;  
        }
        else if(img?.text && img?.text == event.info.text) {
          scaleImg = imgKey;

          return;
        }
        
      })

      if (!scaleImg) {
        console.warn("💻 ~ No hemos podido encontrar la imagen a escalar", event.info.path);
        return;
      }


      // Cambiamos scale, y calculamos los tamaños reales
      // const actualImg = this.imgInfoCanvas3d[scaleImg];
      console.log(`
        DEBUG ESCALA CALLBACK:
        - Width px original: ${this.imgInfoCanvas3d[scaleImg].width}
        - Height px original: ${this.imgInfoCanvas3d[scaleImg].height}
        - Nueva escala: ${event.info.scale}
        - Widht mm actual: ${this.imgInfoCanvas3d[scaleImg].widthMm}
        - Height mm actual: ${this.imgInfoCanvas3d[scaleImg].heightMm}
      `);

      let size = estimateRealMmDimensions(this.activeZone3D, this.imgInfoCanvas3d[scaleImg].width,   this.imgInfoCanvas3d[scaleImg].height, event.info.scale, this.activeZoneMaxSize.Ancho, this.activeZoneMaxSize.Alto)
      console.log("🚀 ~ MM resultantes:", size)
      this.$set(this.imgInfoCanvas3d[scaleImg], "scale", event.info.scale);
      this.$set(this.imgInfoCanvas3d[scaleImg], "heightMm", size.heightMm);
      this.$set(this.imgInfoCanvas3d[scaleImg], "widthMm", size.widthMm);
    },

    /**
     * Callback para manejar la rotación de un elemento en el canvas 3D.
     * 
     * Este método se encarga de identificar la zona 3D y la imagen asociada al evento de rotación,
     * y posteriormente actualiza el estado de rotación del logo correspondiente.
     * 
     * @param {Object} event - Evento de rotación recibido.
     * @param {Object} event.info - Información del evento.
     * @param {string} event.info.idPart - Identificador de la parte 3D asociada.
     * @param {string} event.info.path - Ruta de la imagen asociada al evento.
     * @param {number} event.info.rotation - Ángulo de rotación aplicado.
     * 
     * @returns {void}
     * 
     * @throws {console.warn} - Si no se puede obtener el evento, el id de la zona, la zona 3D, 
     *                          la zona asociada o la imagen a rotar.
     */
    rotateCallBack(event) {
      // Debemos buscar la imagen a la que pertence
      // Primero obtendremos nuestra zona

      if (!event) {
        console.warn("💻 ~ No hemos podido obtener el evento de rotación");
        return;
      }

      if (!event.info?.idPart || (!event.info?.path && !event.info?.text)) {
        console.warn("💻 ~ No hemos podido obtener el id de la zona", event);
        return;
      }

      let zone3D = findZoneBy3dId(event.info?.idPart);

      if (!zone3D) {
        console.warn("💻 ~ No hemos podido encontrar la zona", event.info?.idPart);
        return;
      }

      // Buscamos con los sku a ver si encontramos la zona
      let zone = this.zonas.find((zona) => zona.Posicion == zone3D.sku || zona.Posicion == zone3D.skus[0]);

      if (!zone) {
        console.warn("💻 ~ No hemos podido encontrar la zona", zone3D);
        return;
      }

      let rotateImg;

      // Buscamos la imagen a la que le pertenece con el path y la zona en imgInfoCanvas3d
      Object.keys(this.imgInfoCanvas3d).forEach((imgKey) => {
        let img = this.imgInfoCanvas3d[imgKey];


        if (img.mode == "IMG" && img?.url == event.info.path) {
          rotateImg = imgKey;

          return;  
        }
        else if (img.mode == "TEXT" && img.text == event.info.text) {
          rotateImg = imgKey;

          return;
        }
        
      })

      if (!rotateImg) {
        console.warn("💻 ~ No hemos podido encontrar la imagen a rotar", event.info.path);
        return;
      }
      
      this.rotateLogoState({
        rotate: event.info.rotation,
        zone: zone,
        img: this.imgInfoCanvas3d[rotateImg],
        id: rotateImg
      })

    }
  }
}
</script>

<style lang="scss">
.oktextil-app .canvas3d-wrapper .v-image__image {
  filter: invert(1);
}

.canvas3d-wrapper {
  width: 100%;

  .v-overlay {
    max-height: 780px;
    top: 5px;

    .aviso-canvas-3d {
      color: var(--Color-Grey-800, #282828);
      font-family: $heading-font-family;
      font-size: 0.75rem;
      font-style: italic;
      font-weight: 400;
      line-height: normal;
    }
  }

  .floating-screenshot-btn {
    position: relative;
    bottom: 40px;
    left: 10px;
    z-index: 10;

    .btn-screenshot {
      width: fit-content;
    }
  }
}
#canvas3d,
.canvas3d-wrapper {
  max-height: 675px;
  height: 100%;
}
</style>
